import {
  InlineIconButton,
  RowButton,
  TextButton,
} from "@/components/ui/pressable";
import { useRecipientAutoCommit } from "@/lib/recipientAutoCommit";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  CaretDown,
  CaretUp,
  ListBullets,
  ListNumbers,
  Paperclip,
  PaperPlaneTilt,
  TextB,
  TextItalic,
  TextUnderline,
  X,
} from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { FloatingTextField } from "@/components/ui/floating-field";
import { IconButton } from "@/components/ui/icon-button";
import { Label } from "@/components/ui/label";
import { FileTypeBadge } from "@/lib/fileAttachments";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { sendMailMessage, deleteMailDraft } from "./mailApi";
import { MailHtmlBody } from "./MailHtmlBody";
import { assembleComposeBody } from "./mailQuoteHtml";
import { isMailSendShortcut } from "./mailKeyboardShortcuts";
import type { MailAccount, MailThread } from "./types";

export type MailComposeMode = "new" | "reply" | "reply_all" | "forward";

const MAX_ATTACHMENTS = 5;
const MAX_FILE_BYTES = 4 * 1024 * 1024;
const MAX_TOTAL_BYTES = 8 * 1024 * 1024;

type ComposeAttachment = {
  id: string;
  filename: string;
  content_type: string;
  content_base64: string;
  size_bytes: number;
};

type RecipientChipsHandle = {
  /** Commit any in-progress draft chip and return the full email list. */
  commitPending: () => string[];
};

function exec(cmd: string) {
  document.execCommand(cmd, false);
}

function parseEmails(raw: string): string[] {
  return raw
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function formatBytes(size: number) {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Read failed"));
    reader.readAsDataURL(file);
  });
}

function mergeRecipientEmails(emails: string[], draft: string): string[] {
  const next = parseEmails(draft);
  if (next.length === 0) return emails;
  const merged = [...emails];
  for (const email of next) {
    if (!merged.some((e) => e.toLowerCase() === email.toLowerCase())) {
      merged.push(email);
    }
  }
  return merged;
}

const RecipientChips = forwardRef<
  RecipientChipsHandle,
  {
    id: string;
    label: string;
    emails: string[];
    onChange: (next: string[]) => void;
    trailing?: ReactNode;
    placeholder?: string;
  }
>(function RecipientChips(
  { id, label, emails, onChange, trailing, placeholder = "name@example.com" },
  ref,
) {
  const [draft, setDraft] = useState("");
  const emailsRef = useRef(emails);
  emailsRef.current = emails;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const commit = (value: string) => {
    const parsed = parseEmails(value);
    if (parsed.length === 0) return;
    onChange(mergeRecipientEmails(emails, value));
    setDraft("");
  };

  useRecipientAutoCommit(draft, "email", commit);

  useImperativeHandle(ref, () => ({
    commitPending: () => {
      const merged = mergeRecipientEmails(emailsRef.current, draftRef.current);
      if (parseEmails(draftRef.current).length > 0) {
        onChangeRef.current(merged);
      }
      setDraft("");
      return merged;
    },
  }));

  return (
    <div className="grid gap-2 sm:grid-cols-[4.5rem_1fr] sm:items-start">
      <Label htmlFor={id} className="pt-2 text-muted-foreground">
        {label}
      </Label>
      <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border bg-background px-2 py-1.5 focus-within:ring-1 focus-within:ring-ring">
        {emails.map((email) => (
          <span
            key={email}
            className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
          >
            <span className="truncate">{email}</span>
            <InlineIconButton
              size="xs"
              aria-label={`Remove ${email}`}
              onClick={() => onChange(emails.filter((e) => e !== email))}
            >
              <X className="size-3" weight="bold" />
            </InlineIconButton>
          </span>
        ))}
        <input
          id={id}
          className="min-w-[8rem] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          value={draft}
          placeholder={emails.length === 0 ? placeholder : undefined}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") return;
            if (e.key === "Enter" || e.key === "," || e.key === ";") {
              e.preventDefault();
              commit(draft);
            } else if (e.key === "Backspace" && !draft && emails.length) {
              onChange(emails.slice(0, -1));
            }
          }}
          onBlur={() => {
            if (draft.trim()) commit(draft);
          }}
        />
        {trailing}
      </div>
    </div>
  );
});

function FormatToolbar({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex items-center gap-0.5 rounded-lg border bg-background px-1 py-0.5 shadow-sm",
        className,
      )}
    >
      <IconButton
        aria-label="Bold"
        onMouseDown={(e) => {
          e.preventDefault();
          exec("bold");
        }}
      >
        <TextB className="size-4" />
      </IconButton>
      <IconButton
        aria-label="Italic"
        onMouseDown={(e) => {
          e.preventDefault();
          exec("italic");
        }}
      >
        <TextItalic className="size-4" />
      </IconButton>
      <IconButton
        aria-label="Underline"
        onMouseDown={(e) => {
          e.preventDefault();
          exec("underline");
        }}
      >
        <TextUnderline className="size-4" />
      </IconButton>
      <Separator orientation="vertical" className="mx-1 h-5" />
      <IconButton
        aria-label="Bullet list"
        onMouseDown={(e) => {
          e.preventDefault();
          exec("insertUnorderedList");
        }}
      >
        <ListBullets className="size-4" />
      </IconButton>
      <IconButton
        aria-label="Numbered list"
        onMouseDown={(e) => {
          e.preventDefault();
          exec("insertOrderedList");
        }}
      >
        <ListNumbers className="size-4" />
      </IconButton>
    </div>
  );
}

export function MailComposeDialog({
  open,
  onOpenChange,
  accounts,
  replyTo,
  mode = "new",
  initialTo = "",
  initialCc = "",
  initialSubject = "",
  initialBody = "",
  initialQuotedHtml = null,
  initialInReplyTo,
  initialDraftId = null,
  initialAccountId,
  initialThreadId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accounts: MailAccount[];
  replyTo: MailThread | null;
  mode?: MailComposeMode;
  initialTo?: string;
  initialCc?: string;
  initialSubject?: string;
  initialBody?: string;
  initialQuotedHtml?: string | null;
  initialInReplyTo?: string;
  initialDraftId?: number | null;
  initialAccountId?: number;
  initialThreadId?: number;
}) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const editorShellRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const sendable = accounts.filter(
    (a) => a.status === "connected" || a.status === "error",
  );
  const [accountId, setAccountId] = useState("");
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [bcc, setBcc] = useState<string[]>([]);
  const [subject, setSubject] = useState("");
  const [attachments, setAttachments] = useState<ComposeAttachment[]>([]);
  const [pending, setPending] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState<number | null>(null);
  const [quotedHtml, setQuotedHtml] = useState<string | null>(null);
  const [minimized, setMinimized] = useState(false);
  const [formatPos, setFormatPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const toChipsRef = useRef<RecipientChipsHandle>(null);
  const ccChipsRef = useRef<RecipientChipsHandle>(null);
  const bccChipsRef = useRef<RecipientChipsHandle>(null);

  useEffect(() => {
    if (!open) return;
    setEditingDraftId(initialDraftId ?? null);
    const preferredAccount =
      initialAccountId ?? replyTo?.account_id ?? sendable[0]?.id ?? undefined;
    setAccountId(preferredAccount != null ? String(preferredAccount) : "");
    setTo(parseEmails(initialTo));
    setCc(parseEmails(initialCc));
    setBcc([]);
    setShowCc(Boolean(initialCc));
    setShowBcc(false);
    setAttachments([]);
    setFormatPos(null);
    setMinimized(false);
    setQuotedHtml(initialQuotedHtml ?? null);
    if (initialSubject) {
      setSubject(initialSubject);
    } else if (replyTo?.subject) {
      const base = replyTo.subject.replace(/^(Re|Fwd):\s*/i, "");
      setSubject(mode === "forward" ? `Fwd: ${base}` : `Re: ${base}`);
    } else {
      setSubject("");
    }
    const html = initialBody
      ? initialBody.includes("<")
        ? initialBody
        : `<p>${initialBody.replace(/\n/g, "<br/>")}</p>`
      : "<p><br></p>";
    requestAnimationFrame(() => {
      if (editorRef.current) editorRef.current.innerHTML = html;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    replyTo?.id,
    mode,
    initialTo,
    initialCc,
    initialSubject,
    initialBody,
    initialQuotedHtml,
    initialDraftId,
    initialAccountId,
  ]);

  const updateFormatToolbar = useCallback(() => {
    const sel = window.getSelection();
    const editor = editorRef.current;
    const shell = editorShellRef.current;
    if (!sel || !editor || !shell || sel.isCollapsed || sel.rangeCount === 0) {
      setFormatPos(null);
      return;
    }
    const anchor = sel.anchorNode;
    if (!anchor || !editor.contains(anchor)) {
      setFormatPos(null);
      return;
    }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const shellRect = shell.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      setFormatPos(null);
      return;
    }
    setFormatPos({
      top: Math.max(8, rect.top - shellRect.top - 44),
      left: Math.min(
        Math.max(8, rect.left - shellRect.left + rect.width / 2 - 90),
        shellRect.width - 200,
      ),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onSel = () => updateFormatToolbar();
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  }, [open, updateFormatToolbar]);

  const title =
    mode === "forward"
      ? "Forward"
      : mode === "reply_all"
        ? "Reply all"
        : mode === "reply"
          ? "Reply"
          : "New email";

  const addFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    const next = [...attachments];
    let total = next.reduce((sum, a) => sum + a.size_bytes, 0);
    for (const file of list) {
      if (next.length >= MAX_ATTACHMENTS) {
        notify(`You can attach up to ${MAX_ATTACHMENTS} files`, {
          type: "warning",
        });
        break;
      }
      if (file.size > MAX_FILE_BYTES) {
        notify(`"${file.name}" exceeds ${MAX_FILE_BYTES / (1024 * 1024)} MB`, {
          type: "warning",
        });
        continue;
      }
      if (total + file.size > MAX_TOTAL_BYTES) {
        notify(
          `Attachments exceed ${MAX_TOTAL_BYTES / (1024 * 1024)} MB total`,
          { type: "warning" },
        );
        break;
      }
      try {
        const content_base64 = await fileToBase64(file);
        next.push({
          id: crypto.randomUUID(),
          filename: file.name,
          content_type: file.type || "application/octet-stream",
          content_base64,
          size_bytes: file.size,
        });
        total += file.size;
      } catch {
        notify(`Could not read "${file.name}"`, { type: "error" });
      }
    }
    setAttachments(next);
  };

  const handleDiscardDraft = async () => {
    if (!editingDraftId) {
      onOpenChange(false);
      return;
    }
    if (!window.confirm("Discard this draft? This cannot be undone.")) {
      return;
    }
    setPending(true);
    try {
      await deleteMailDraft(editingDraftId);
      notify("Draft discarded", { type: "success" });
      void queryClient.invalidateQueries({ queryKey: ["mail_threads"] });
      onOpenChange(false);
    } catch (e) {
      notify(e instanceof Error ? e.message : "Could not discard draft", {
        type: "error",
      });
    } finally {
      setPending(false);
    }
  };

  const handleSend = async (asDraft = false) => {
    const id = Number(accountId);
    const nextTo = toChipsRef.current?.commitPending() ?? to;
    const nextCc = ccChipsRef.current?.commitPending() ?? cc;
    const nextBcc = bccChipsRef.current?.commitPending() ?? bcc;
    setTo(nextTo);
    setCc(nextCc);
    setBcc(nextBcc);
    const userHtml = editorRef.current?.innerHTML?.trim() || "<p></p>";
    const bodyHtml = assembleComposeBody(userHtml, quotedHtml);
    if (!id || (!asDraft && nextTo.length === 0)) {
      notify("Choose a From account and at least one recipient", {
        type: "warning",
      });
      return;
    }
    setPending(true);
    try {
      const result = await sendMailMessage({
        account_id: id,
        to: nextTo,
        cc: nextCc,
        bcc: nextBcc.length ? nextBcc : undefined,
        subject,
        body_html: bodyHtml,
        thread_id:
          mode === "forward"
            ? undefined
            : (replyTo?.id ?? initialThreadId ?? undefined),
        in_reply_to:
          mode === "forward" || mode === "new" ? undefined : initialInReplyTo,
        save_draft: asDraft,
        draft_id: editingDraftId ?? undefined,
        attachments: asDraft
          ? undefined
          : attachments.map(
              ({ filename, content_type, content_base64, size_bytes }) => ({
                filename,
                content_type,
                content_base64,
                size_bytes,
              }),
            ),
      });
      if (asDraft && result.draft_id) {
        setEditingDraftId(result.draft_id);
      }
      notify(asDraft ? "Draft saved" : "Message sent", { type: "success" });
      void queryClient.invalidateQueries({ queryKey: ["mail_threads"] });
      void queryClient.invalidateQueries({ queryKey: ["mail_messages"] });
      void queryClient.invalidateQueries({ queryKey: ["mail_attachments"] });
      void queryClient.invalidateQueries({ queryKey: ["mail_unread_count"] });
      void queryClient.invalidateQueries({ queryKey: ["mail_accounts_safe"] });
      if (!asDraft) {
        onOpenChange(false);
      }
    } catch (e) {
      notify(e instanceof Error ? e.message : "Send failed", { type: "error" });
    } finally {
      setPending(false);
    }
  };

  const handleSendRef = useRef(handleSend);
  handleSendRef.current = handleSend;
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isMailSendShortcut(event) || pendingRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      void handleSendRef.current(false);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[80] flex justify-end sm:p-3 sm:pr-4"
      role="presentation"
    >
      <section
        aria-label={title}
        className={cn(
          "pointer-events-auto flex w-full flex-col overflow-hidden border border-border bg-background shadow-overlay",
          "rounded-t-xl sm:rounded-xl",
          minimized
            ? "h-12 max-w-none sm:w-[360px]"
            : "h-[min(100dvh,720px)] max-h-[100dvh] sm:h-[min(640px,calc(100dvh-1.5rem))] sm:w-[min(560px,calc(100vw-1.5rem))]",
        )}
      >
        <header className="flex shrink-0 items-center gap-2 border-b bg-foreground px-3 py-2 text-background">
          <RowButton
            layout="none"
            density="none"
            className="min-w-0 flex-1 truncate text-left text-sm font-medium"
            onClick={() => setMinimized((value) => !value)}
          >
            {title}
            {subject.trim() ? (
              <span className="ml-2 font-normal text-background/70">
                — {subject.trim()}
              </span>
            ) : null}
          </RowButton>
          <IconButton
            aria-label={minimized ? "Expand compose" : "Minimize compose"}
            className="size-8 text-background/90 hover:bg-background/10 hover:text-background"
            onClick={() => setMinimized((value) => !value)}
          >
            {minimized ? (
              <CaretUp className="size-4" weight="bold" />
            ) : (
              <CaretDown className="size-4" weight="bold" />
            )}
          </IconButton>
          <IconButton
            aria-label="Close compose"
            className="size-8 text-background/90 hover:bg-background/10 hover:text-background"
            onClick={() =>
              editingDraftId ? void handleDiscardDraft() : onOpenChange(false)
            }
          >
            <X className="size-4" weight="bold" />
          </IconButton>
        </header>

        {!minimized ? (
          <>
            <div className="min-h-0 shrink-0 space-y-3 overflow-y-auto px-4 py-3">
              <div className="grid gap-2 sm:grid-cols-[4.5rem_1fr] sm:items-center">
                <Label className="text-muted-foreground">From</Label>
                <Select value={accountId} onValueChange={setAccountId}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select mailbox" />
                  </SelectTrigger>
                  <SelectContent>
                    {sendable.length === 0 ? (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">
                        No sendable mailboxes. Fix sync errors in Settings →
                        Mailboxes.
                      </div>
                    ) : (
                      sendable.map((account) => (
                        <SelectItem key={account.id} value={String(account.id)}>
                          {account.email}
                          {account.status !== "connected"
                            ? " (Sync issue)"
                            : ""}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <RecipientChips
                ref={toChipsRef}
                id="mail-to"
                label="To"
                emails={to}
                onChange={setTo}
                trailing={
                  <div className="ml-auto flex shrink-0 gap-1">
                    {!showCc ? (
                      <TextButton
                        tone="muted"
                        size="xs"
                        className="px-1.5"
                        onClick={() => setShowCc(true)}
                      >
                        Cc
                      </TextButton>
                    ) : null}
                    {!showBcc ? (
                      <TextButton
                        tone="muted"
                        size="xs"
                        className="px-1.5"
                        onClick={() => setShowBcc(true)}
                      >
                        Bcc
                      </TextButton>
                    ) : null}
                  </div>
                }
              />

              {showCc ? (
                <RecipientChips
                  ref={ccChipsRef}
                  id="mail-cc"
                  label="Cc"
                  emails={cc}
                  onChange={setCc}
                  placeholder="optional"
                />
              ) : null}
              {showBcc ? (
                <RecipientChips
                  ref={bccChipsRef}
                  id="mail-bcc"
                  label="Bcc"
                  emails={bcc}
                  onChange={setBcc}
                  placeholder="optional"
                />
              ) : null}

              <FloatingTextField
                id="mail-subject"
                label="Subject"
                value={subject}
                onChange={setSubject}
                placeholder="Subject"
              />
            </div>

            <div
              ref={editorShellRef}
              className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-t"
            >
              {formatPos ? (
                <div
                  className="pointer-events-auto absolute z-10"
                  style={{ top: formatPos.top, left: formatPos.left }}
                >
                  <FormatToolbar />
                </div>
              ) : null}
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                data-placeholder="Write your message…"
                onMouseUp={updateFormatToolbar}
                onKeyUp={updateFormatToolbar}
                className={cn(
                  "mail-compose-editor shrink-0 overflow-x-hidden overflow-y-auto px-4 py-3 text-sm leading-relaxed outline-none",
                  quotedHtml
                    ? "max-h-[28vh] min-h-[72px]"
                    : "min-h-[200px] flex-1",
                  "[&_a]:text-primary [&_a]:underline [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-0 [&_p+p]:mt-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5",
                  "empty:before:pointer-events-none empty:before:text-muted-foreground empty:before:content-[attr(data-placeholder)]",
                )}
              />
              {quotedHtml ? (
                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                  <div className="mail-compose-quote-pane min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-2">
                    <MailHtmlBody
                      html={quotedHtml}
                      variant="embedded"
                      layout="auto"
                    />
                  </div>
                </div>
              ) : null}
            </div>

            {attachments.length > 0 ? (
              <div className="space-y-2 border-t px-4 py-3">
                <p className="text-xs font-medium text-muted-foreground">
                  {attachments.length} attachment
                  {attachments.length === 1 ? "" : "s"}
                </p>
                <ul className="space-y-2">
                  {attachments.map((file) => (
                    <li
                      key={file.id}
                      className="flex items-center gap-3 rounded-lg border bg-muted/20 px-3 py-2"
                    >
                      <FileTypeBadge
                        file={{
                          title: file.filename,
                          type: file.content_type,
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {file.filename}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatBytes(file.size_bytes)}
                        </p>
                      </div>
                      <IconButton
                        aria-label={`Remove ${file.filename}`}
                        onClick={() =>
                          setAttachments((prev) =>
                            prev.filter((a) => a.id !== file.id),
                          )
                        }
                      >
                        <X className="size-4" />
                      </IconButton>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) void addFiles(e.target.files);
                e.target.value = "";
              }}
            />

            <footer className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t px-4 py-3">
              <div className="flex items-center gap-1">
                <IconButton
                  aria-label="Attach files"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={pending || attachments.length >= MAX_ATTACHMENTS}
                >
                  <Paperclip className="size-4" />
                </IconButton>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    editingDraftId
                      ? void handleDiscardDraft()
                      : onOpenChange(false)
                  }
                  disabled={pending}
                >
                  {editingDraftId ? "Discard draft" : "Cancel"}
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void handleSend(true)}
                  disabled={pending}
                >
                  Save draft
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => void handleSend(false)}
                  disabled={pending}
                  isLoading={pending}
                >
                  <PaperPlaneTilt className="size-4" weight="fill" />
                  Send
                </Button>
              </div>
            </footer>
          </>
        ) : null}
      </section>
    </div>,
    document.body,
  );
}
